<script setup>
import VButton from '@/modules/Signup/components/VButton.vue';
import { computed, ref, unref } from 'vue';
import VIconRender from '@/modules/Signup/components/VIconRender.vue';
import LeftChevronIcon from '@/modules/Signup/__clients/ezi/icons/left-chevron.svg';
import RightChevronIcon from '@/modules/Signup/__clients/ezi/icons/right-chevron.svg';
import DescriptiveList from '@/modules/Signup/components/pricing-plans/DescriptiveList.vue';
import { currencyFormatter, formatArray } from '../../utils';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';


const
    signUpStore = useSignUpModuleStore(),
    { selectedPlan } = storeToRefs(signUpStore);

// eslint-disable-next-line no-undef
const props = defineProps({
    // Not required, as to make the component flexible through slot.
    plans: {
        type: [Array, Object],
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: true,
    },
    selectedPlanId: {
        type: [String, Number],
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['select-plan-product']);

const responsiveOptions = ref([
    {
        breakpoint: '1400px',
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: '1199px',
        numVisible: 3,
        numScroll: 1,
    },
    {
        breakpoint: '767px',
        numVisible: 2,
        numScroll: 1,
    },
    {
        breakpoint: '575px',
        numVisible: 1,
        numScroll: 1,
    },
]);

const showNavigators = computed(() => props.plans?.length > 3);

const handleSelectedButton = (id) => {
    const selectedPlan = props.selectedPlanId === id;
    return {
        label: `Select${selectedPlan ? 'ed!' : ''}`,
        background: selectedPlan ?
            '!tw-bg-[linear-gradient(75.47deg,_#FF0090_10.53%,_#FF5064_86.98%)]' :
            '!tw-bg-secondary',
        border: selectedPlan ? 'tw-border-[#FF0090]' : 'tw-border-[#312C704D]',
    };
};

// Formatted to a more readable format.
const listOfTechnologies = computed(() => (
    `Connects with ${formatArray(unref(selectedPlan)?.properties.technologies)}`
));
</script>

<template>
    <div class="sign-up__component-pricing-plans tw-relative tw-overflow-hidden">
        <!-- Background -->
        <div class="tw-bg-[#FF50640D] tw-w-full tw-h-[80%] tw-top-[10%] tw-left-0 tw-absolute"></div>

        <!-- Diagonal BG -->
        <div class="tw-absolute tw-top-[10%] tw-left-0 tw-w-full tw-h-[80%] tw-overflow-hidden">
            <div
                class="tw-w-[100%] tw-h-[150%] -tw-mt-28 tw-bg-[#FF5064] tw-opacity-[7%] tw-transform tw-rotate-[-25deg]
            tw-origin-top-right"
            ></div>
        </div>

        <!-- Disabled State -->
        <div
            v-if="props.disabled"
            class="tw-h-full tw-w-full tw-absolute tw-top-0 tw-left-0 tw-z-10 tw-bg-[rgba(255,255,255,0.55)]"
        ></div>

        <div class="tw-w-2/3 tw-mx-auto">
            <Carousel
                :value="plans"
                :numVisible="3"
                :numScroll="3"
                :responsiveOptions="responsiveOptions"
                :showIndicators="false"
                :showNavigators="showNavigators"
                containerClass="tw-font-raleway"
            >
                <template #previousicon>
                    <VIconRender :icon-src="LeftChevronIcon" />
                </template>
                <template #nexticon>
                    <VIconRender :icon-src="RightChevronIcon" />
                </template>

                <template #item="{ data }">
                    <div class="tw-group tw-bg-white tw-relative tw-z-10">
                        <div
                            class="tw-border tw-border-solid group-hover:tw-border-[#FF0090]"
                            :class="[handleSelectedButton(data?.ID).border]"
                        >
                            <header class="tw-pt-3 tw-text-center">
                                <h4
                                    class="tw-font-neue-kabel-bold tw-uppercase !tw-text-[32px] tw-leading-relaxed"
                                >
                                    {{ data.Name }}
                                </h4>
                                <div
                                    class="tw-text-[19px] tw-text-primary tw-bg-[#FF00901A] tw-py-1 tw-mt-2
                                    tw-font-medium"
                                >
                                    {{ listOfTechnologies }}
                                </div>
                            </header>

                            <div v-html="data.Properties.ProductImage" class="!tw-m-6"></div>

                            <div class="tw-flex tw-flex-col tw-gap-7">
                                <div class="tw-flex tw-flex-col tw-gap-5">
                                    <div
                                        class="tw-text-white tw-w-max tw-text-[32px] tw-font-bold tw-px-8 tw-py-2
                                        tw-bg-[linear-gradient(65.1deg,_#FF0090_-19.09%,_#FF7D90_91.65%)]"
                                    >
                                        {{ currencyFormatter(data.Price?.Amount) }}&nbsp;<span class="tw-text-[14px]">inc. shipping</span>
                                    </div>
                                </div>

                                <!-- Section: Plan Information -->
                                <section class="tw-text-center tw-flex tw-flex-col tw-gap-8">
                                    <header>
                                        <h5 class="!tw-font-bold !tw-text-[14px]">
                                            {{ data.Properties.ProductPlanInformation.WifiReachText }}
                                        </h5>
                                        <span class="tw-font-medium tw-text-primary tw-inline-block tw-mt-1">
                                            {{ data.Properties.ProductPlanInformation.ReachInformation }}
                                        </span>
                                    </header>

                                    <div>
                                        <figure class="tw-flex tw-justify-center tw-gap-4 tw-w-1/3 !tw-mx-auto">
                                            <img
                                                :src="data.Properties.ProductPlanInformation.Image"
                                                alt=""
                                            />
                                            <ul class="tw-text-left tw-flex tw-flex-col tw-gap-4 tw-font-medium !tw-mt-2">
                                                <li
                                                    v-for="(detail, detailIndex) in data.Properties.ProductPlanInformation.Details"
                                                    :key="detailIndex"
                                                >
                                                    {{ detail }}
                                                </li>
                                            </ul>
                                        </figure>
                                    </div>
                                </section>

                                <!-- Section: Plan Benefit -->
                                <DescriptiveList :items="data.Properties.ProductPlanBenefits.Details" />

                                <!-- Section: Disclaimer -->
                                <section class="tw-px-6">
                                    <p>
                                        {{ data.Properties.ProductPlanInformation.ReachInformation }}
                                    </p>
                                </section>

                                <!-- Section: Datasheet -->
                                <ul
                                    v-if="data.ProductPlanBenefits?.DataSheet"
                                    class="tw-text-center tw-flex tw-flex-col tw-gap-2"
                                >
                                    <li>
                                        <a
                                            :href="data.ProductPlanBenefits.DataSheet"
                                            class="!tw-text-[#FF5064] !tw-underline tw-inline-block"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Relevant Link here if required
                                        </a>
                                    </li>
                                </ul>
                            </div>

                            <footer class="tw-flex tw-justify-between tw-items-end tw-px-6 tw-py-5">
                                <span class="tw-inline-block tw-mb-1">Pricing Includes GST</span>
                                <VButton
                                    @click="emit('select-plan-product', data)"
                                    :label="handleSelectedButton(data?.ID).label"
                                    :bg-class="handleSelectedButton(data?.ID).background"
                                />
                            </footer>
                        </div>
                    </div>
                </template>
            </Carousel>
        </div>
    </div>
</template>
